import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  baseCssConfigStyle,
  ContainerType,
  LayoutNames,
  NewUiZoneWidgetConfig,
  ProfileStateModel,
  UiPageConfig,
  UserInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { ComponentInjectorConfig, WidgetLayoutService } from './../widget-layout.service';

import { ActivatedRoute, ParamMap } from '@angular/router';
import { DroppedWidgetIntoLayoutWidgetAction } from '@app/finxone-web-frontend/app/lib/actions/dropped.action';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import { CloseModalPopupAction } from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { MultiPartFormHolderService } from '@app/finxone-web-frontend/app/lib/services/multipart-holder/multipart-form-holder.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { debounceTime, delay, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { fetchProfileAndSetOrgInfoBasedOnZone, getRouteParams, resetStylesOnWebApp } from '../../utils';

@Component({
  selector: 'finxone-web-frontend-footer-layout',
  templateUrl: './footer-layout.component.html',
  styleUrls: ['./footer-layout.component.scss'],
  standalone: false,
})
export class FooterLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() footerLayoutStyles = {};
  @ViewChild('footerLayoutContainer') footer!: ElementRef;
  public urlParams: {
    zoneUrlInput: string;
    pageUrlInput: string;
    roleInput: string;
    subscriptionInput: string;
    idInput: string;
  };
  public isWorkflowType: boolean;
  public currentPageDetail: UiPageConfig | null | undefined;
  public userInfo: UserInfoType;
  @Select(ProfileState.getProfile)
  public profile$!: Observable<ProfileStateModel>;
  public profile: ProfileStateModel;
  public injectorComponents: {
    [key: string]: ComponentInjectorConfig;
  } = {};
  public topPadding = '0px';
  public bottomPadding = '0px';
  public mouseOver = '';
  public mouseOverId = '';
  public pageMargins = {};
  public footerOuterCss: { [key: string]: string };
  private params: ParamMap;
  get injectorArray() {
    return Object.values(this.injectorComponents);
  }
  public footerLayoutHeight = 0;

  constructor(
    public configService: ConfigService,
    private route: ActivatedRoute,
    private store: Store,
    private formHolderService: MultiPartFormHolderService,
    private widgetLayoutService: WidgetLayoutService,
    private actions: Actions,
  ) {
    resetStylesOnWebApp();
    getRouteParams(this.route)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          this.urlParams = params;
          return fetchProfileAndSetOrgInfoBasedOnZone(this.profile$, this.store, this.urlParams.zoneUrlInput);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((profile) => {
        if (profile) {
          this.profile = profile;
        }
      });

    this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;

    // Clear any previously loaded form stages to ensure a clean state before loading widgets and page configuration.
    this.formHolderService.setFormStages([]);
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(distinctUntilChanged(), debounceTime(100), takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.params = params;
        this.widgetLayoutService.initWidgetLayout(
          this.urlParams,
          this.route,
          params,
          this.profile,
          LayoutNames.FOOTER_LAYOUT,
        );
        this.getFooterWidgetLayout();
      });
    this.getFooterWidgetLayout();
    this.handleCloseModalPopup();
  }

  ngAfterViewInit() {
    this.updateFooterHeight();
  }

  /**
   * Listens for the modal close action and updates the page layout accordingly.
   *
   * When a modal is closed, this method checks whether the closed view was a modal
   * or a regular page. It updates the current URL parameters, re-initializes the
   * widget layout for the page, and reloads the footer widgets to ensure the UI
   * reflects the current page state.
   *
   */
  private handleCloseModalPopup() {
    this.actions
      .pipe(ofActionDispatched(CloseModalPopupAction), delay(100), takeUntil(this.destroy$))
      .subscribe((action: { isModal: boolean }) => {
        this.urlParams.pageUrlInput = action.isModal
          ? this.route.snapshot.params?.['modal-page']
          : this.route.snapshot.params?.['page'];
        this.widgetLayoutService.initWidgetLayout(
          this.urlParams,
          this.route,
          this.params,
          this.profile,
          LayoutNames.FOOTER_LAYOUT,
        );
        this.getFooterWidgetLayout();
      });
  }

  public updateHoverState($event: MouseEvent, mouseOverId?: string) {
    this.mouseOver = $event.type == 'mouseover' ? 'widget-hover' : '';
    if (mouseOverId) this.mouseOverId = mouseOverId;
  }

  public selectWidget(event: MouseEvent, widget: NewUiZoneWidgetConfig) {
    if (typeof widget.uniqueId === 'number') {
      widget.uniqueId = widget.uniqueId.toString();
    }

    const uniqueId = widget.uniqueId;
    if (!uniqueId) {
      console.error('Widget uniqueId is undefined or null');
      return;
    }

    if (this.isWorkflowType) {
      if (event.shiftKey) {
        this.store.dispatch(new SelectWidget(uniqueId, true));
      } else {
        this.store.dispatch(new SelectWidget(uniqueId, false));
      }
    }
    event.stopPropagation();
  }

  public getWidgetClasses = getWidgetClasses;

  private getFooterWidgetLayout() {
    this.widgetLayoutService
      .loadWidgetComponentsByLayoutType(this.urlParams, this.profile, LayoutNames.FOOTER_LAYOUT)
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe((injectorComponents) => {
        this.injectorComponents = injectorComponents;
        setTimeout(() => {
          this.updateFooterHeight();
        });
        this.loadPageContent();
      });
  }

  /**
   * Measures the footer widget height and updates the layout state.
   * This ensures the main content can adjust its height and avoid overlapping
   * with sticky footers.
   */
  private updateFooterHeight(): void {
    this.footerLayoutHeight = this.footer.nativeElement.offsetHeight;
    this.widgetLayoutService.setFooterLayoutHeight(this.footerLayoutHeight);
  }

  private loadPageContent() {
    this.widgetLayoutService.pageContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => this.updateFooterStyles(result));
  }

  private updateFooterStyles(result: { style: baseCssConfigStyle; pageDetail: UiPageConfig | null } | null) {
    this.footerOuterCss = {};
    if (!result) return;

    this.currentPageDetail = result?.pageDetail;
    const pageMargin = this.getPageMargins(result?.style);

    this.footerOuterCss = { ...pageMargin, ...this.footerLayoutStyles };
  }

  public getPageMargins(style: baseCssConfigStyle = {}): baseCssConfigStyle {
    if (
      this.currentPageDetail?.containerType === ContainerType.PAGE &&
      this.currentPageDetail?.footerLayoutEnabled &&
      !this.currentPageDetail?.footerConfiguration?.ignorePageMargins
    ) {
      return style;
    }
    return {};
  }

  public drop(widgetDroppedInto: NewUiZoneWidgetConfig) {
    this.store.dispatch(
      new DroppedWidgetIntoLayoutWidgetAction(widgetDroppedInto, LayoutNames.FOOTER_LAYOUT),
    );
  }

  public trackByFooterWidgetLayouts(index: number, component: { widget: NewUiZoneWidgetConfig }): string {
    return component.widget.uniqueId.toString();
  }

  ngOnDestroy() {
    Object.values(this.injectorComponents).forEach((componentRef) => {
      if (componentRef && componentRef.component.destroy) {
        componentRef.component.destroy();
      }
    });
    this.injectorComponents = {};
  }
}
